import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Couleur } from '@nextilt/commun';
import { api } from '../partage/api/client';
import type { ChampsALaNaissance } from '../partage/api/types';
import { creerOuMettreEnFile, OPTIONS_DE_CREATION } from '../partage/creation';
import { enMots } from '../maintenant';
import { relireApres } from '../partage/relectures';
import { utilisateurConnecte } from '../partage/useArbre';

/**
 * Les écritures du + du téléphone (chantier `telephone-projets-fiche`, ticket 05) : créer la
 * bulle réglée de ce qu'elle est, en une demande, la mettre dans une journée ; créer un projet avec sa couleur ;
 * garder une tâche possible dans la journée d'aujourd'hui. Chaque geste relit tout ce qu'il
 * change (`relectures.ts`) : les feuilles, les projets, « Maintenant », l'Agenda et la journée.
 */

export interface AjoutParLePlus {
  titre: string;
  parentId: string;
  /** Ce avec quoi elle naît (`champsDeLaSorte`), dans la même demande ; `null` : une simple tâche. */
  champs: ChampsALaNaissance | null;
  /** La journée où elle entre (`jourDeLaJournee`), `null` : aucune. */
  journee: string | null;
  /** Aujourd'hui chez l'utilisateur : la journée d'aujourd'hui passe par « Garder pour aujourd'hui ». */
  aujourdhui: string;
}

/**
 * Ce que le geste a donné : `horsLigne`, la bulle attend le réseau dans la file ;
 * `refusDuJour`, la bulle est créée mais la journée l'a refusée (dit en mots).
 */
export interface FaitParLePlus {
  horsLigne: boolean;
  refusDuJour: string | null;
}

/**
 * Ajouter par le +. Sans réseau, une Tâche ou une Idée part dans la file hors ligne (le seul
 * geste qui survit sans réseau, docs/adr/0001), sans journée : la journée attend le réseau. Un
 * Rendez-vous ou une Échéance ne se crée pas hors ligne, l'écran les a déjà grisés.
 */
export function useAjouterParLePlus() {
  const client = useQueryClient();
  return useMutation({
    ...OPTIONS_DE_CREATION,
    mutationFn: (ajout: AjoutParLePlus) => ajouterParLePlus(ajout, () => utilisateurConnecte(client)),
    // Créée, et peut-être entrée dans une journée : `creer` relit aussi le jour.
    onSettled: () => void relireApres(client, 'creer'),
  });
}

/**
 * Le geste du + : la bulle naît réglée, en une seule demande (chantier nettoyage-fiche, ticket
 * 03), puis entre dans sa journée. Un refus du serveur ne crée rien et remonte tel quel.
 * `utilisateur` : qui est connecté, lu seulement quand le réseau manque.
 */
export async function ajouterParLePlus(ajout: AjoutParLePlus, utilisateur: () => string | null): Promise<FaitParLePlus> {
  // Seules une Tâche et une Idée attendent le réseau : la file ne garde que le titre et l'Idée.
  const simple = ajout.champs === null || ajout.champs.etat === 'sans_objet';
  const creee = await creerOuMettreEnFile(
    { titre: ajout.titre, parentId: ajout.parentId, champs: ajout.champs ?? {}, file: simple },
    utilisateur
  );
  if (!('id' in creee)) return { horsLigne: true, refusDuJour: null };
  const id = creee.id;
  if (!ajout.journee) return { horsLigne: false, refusDuJour: null };
  try {
    await mettreDansLaJournee(id, ajout.journee, ajout.aujourdhui);
    return { horsLigne: false, refusDuJour: null };
  } catch (erreur) {
    return { horsLigne: false, refusDuJour: enMots(erreur) };
  }
}

/**
 * Une tâche de plus dans la journée d'un jour (cadrage, décision 9 : le jour devient une journée
 * prévue). Aujourd'hui : « Garder pour aujourd'hui », à la suite, dès maintenant. Un jour à venir :
 * le serveur l'ajoute à la suite (`POST /api/journees/:jour/taches`, chantier nettoyage-gestes,
 * ticket 03) ; l'écran ne renvoie plus la journée entière.
 */
async function mettreDansLaJournee(id: string, jour: string, aujourdhui: string): Promise<void> {
  if (jour === aujourdhui) {
    await api.garderPourAujourdhui(id);
    return;
  }
  await api.ajouterAUnJour(jour, id);
}

/**
 * Créer un projet avec la couleur choisie. Sans réseau, il part dans la file comme avant, sans sa
 * couleur (la file ne garde que le titre) : le tourniquet la donnera.
 */
export function useCreerUnProjet() {
  const client = useQueryClient();
  return useMutation({
    ...OPTIONS_DE_CREATION,
    mutationFn: async ({ titre, couleur }: { titre: string; couleur: Couleur }): Promise<string | null> => {
      const creee = await creerOuMettreEnFile({ titre, parentId: null, champs: { couleur }, file: true }, () =>
        utilisateurConnecte(client)
      );
      return 'id' in creee ? creee.id : null;
    },
    onSettled: () => void relireApres(client, 'creer'),
  });
}

/** Une tâche choisie parmi les possibles entre dans la journée d'aujourd'hui. */
export function useGarderParLePlus() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.garderPourAujourdhui(id),
    onSettled: () => void relireApres(client, 'garderPourAujourdhui'),
  });
}
